import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin, Clock, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Primitives";
import { CONTACT, HOURS, SERVICE_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", email: "", phone: "", service: "", preferred_date: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [mapConsent, setMapConsent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Bitte Name und E-Mail angeben.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      toast.success("Vielen Dank! Ihre Anfrage wurde übermittelt. Ich melde mich in Kürze bei Ihnen.");
      setForm(emptyForm);
    } catch (err) {
      toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder rufen Sie an.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-gold/40 py-3 text-charcoal placeholder:text-charcoal-muted/70 focus:outline-none focus:border-gold-deep transition-colors duration-300";

  return (
    <section id="kontakt" className="relative py-24 lg:py-36 bg-cream">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        {/* Left: intro + form */}
        <div>
          <Reveal>
            <Eyebrow>Terminvereinbarung</Eyebrow>
            <h2 className="mt-6 font-serif font-light tracking-tight text-charcoal text-4xl sm:text-5xl leading-tight">
              Ich freue mich auf Sie
            </h2>
            <p className="mt-6 text-charcoal-soft leading-relaxed max-w-lg">
              Vereinbaren Sie Ihren persönlichen Termin oder stellen Sie mir Ihre Fragen. Füllen Sie einfach
              das Formular aus – ich melde mich zeitnah bei Ihnen zurück.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="mt-10 space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <input data-testid="input-name" value={form.name} onChange={update("name")} placeholder="Ihr Name*" className={inputCls} />
                <input data-testid="input-email" type="email" value={form.email} onChange={update("email")} placeholder="E-Mail*" className={inputCls} />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <input data-testid="input-phone" value={form.phone} onChange={update("phone")} placeholder="Telefon" className={inputCls} />
                <select data-testid="input-service" value={form.service} onChange={update("service")} className={`${inputCls} appearance-none`}>
                  <option value="">Leistung wählen</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <input data-testid="input-date" value={form.preferred_date} onChange={update("preferred_date")} placeholder="Wunschtermin (z. B. KW 24, vormittags)" className={inputCls} />
              <textarea data-testid="input-message" value={form.message} onChange={update("message")} placeholder="Ihre Nachricht" rows={4} className={`${inputCls} resize-none`} />
              <button
                type="submit"
                data-testid="submit-appointment"
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-full bg-charcoal text-gold-soft px-8 py-4 text-sm tracking-wide hover:bg-charcoal-soft transition-colors duration-300 disabled:opacity-60"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                Anfrage senden
              </button>
            </form>
          </Reveal>
        </div>

        {/* Right: contact details, hours, map */}
        <Reveal delay={0.15}>
          <div className="bg-cream-soft rounded-[2rem] border border-gold/20 p-8 lg:p-10">
            <div className="space-y-5">
              <a data-testid="contact-phone" href={CONTACT.phoneHref} className="flex items-center gap-4 group">
                <span className="h-11 w-11 rounded-full bg-cream flex items-center justify-center border border-gold/30"><Phone size={18} strokeWidth={1.5} className="text-gold-deep" /></span>
                <span><span className="block text-[11px] uppercase tracking-widest text-charcoal-muted">Telefon</span><span className="text-charcoal group-hover:text-gold-deep transition-colors">{CONTACT.phone}</span></span>
              </a>
              <a data-testid="contact-whatsapp" href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" className="flex items-center gap-4 group">
                <span className="h-11 w-11 rounded-full bg-cream flex items-center justify-center border border-gold/30"><MessageCircle size={18} strokeWidth={1.5} className="text-gold-deep" /></span>
                <span><span className="block text-[11px] uppercase tracking-widest text-charcoal-muted">WhatsApp</span><span className="text-charcoal group-hover:text-gold-deep transition-colors">{CONTACT.whatsapp}</span></span>
              </a>
              <a data-testid="contact-email" href={CONTACT.emailHref} className="flex items-center gap-4 group">
                <span className="h-11 w-11 rounded-full bg-cream flex items-center justify-center border border-gold/30"><Mail size={18} strokeWidth={1.5} className="text-gold-deep" /></span>
                <span><span className="block text-[11px] uppercase tracking-widest text-charcoal-muted">E-Mail</span><span className="text-charcoal group-hover:text-gold-deep transition-colors">{CONTACT.email}</span></span>
              </a>
              <div className="flex items-center gap-4">
                <span className="h-11 w-11 rounded-full bg-cream flex items-center justify-center border border-gold/30"><MapPin size={18} strokeWidth={1.5} className="text-gold-deep" /></span>
                <span><span className="block text-[11px] uppercase tracking-widest text-charcoal-muted">Adresse</span><span className="text-charcoal">{CONTACT.addressLine1}, {CONTACT.addressLine2}</span></span>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-gold/20">
              <div className="flex items-center gap-2 text-charcoal mb-4">
                <Clock size={16} strokeWidth={1.5} className="text-gold-deep" />
                <span className="text-[11px] uppercase tracking-widest text-charcoal-muted">Öffnungszeiten</span>
              </div>
              <ul className="space-y-2">
                {HOURS.map((h) => (
                  <li key={h.day} className="flex justify-between text-sm">
                    <span className="text-charcoal-soft">{h.day}</span>
                    <span className="text-charcoal">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 overflow-hidden rounded-2xl ring-1 ring-gold/20">
              {mapConsent ? (
                <iframe
                  title="Standort der Praxis"
                  data-testid="map"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapsQuery)}&output=embed`}
                  className="w-full h-56 border-0"
                  loading="lazy"
                />
              ) : (
                <button
                  data-testid="map-consent"
                  onClick={() => setMapConsent(true)}
                  className="w-full h-56 bg-cream flex flex-col items-center justify-center gap-3 text-center px-6 hover:bg-cream-deep/40 transition-colors duration-300"
                >
                  <MapPin size={26} strokeWidth={1.5} className="text-gold-deep" />
                  <span className="text-sm text-charcoal">Karte laden (Google Maps)</span>
                  <span className="text-xs text-charcoal-muted max-w-xs">
                    Mit dem Laden akzeptieren Sie, dass Daten an Google übertragen werden. Siehe Datenschutzerklärung.
                  </span>
                </button>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
